import { memo, useCallback } from 'react';
import classNames from 'classnames';
import { BiPencil } from 'react-icons/bi';
import { TbCirclePlus } from 'react-icons/tb';
import {
  V2QsCollapsibleProps,
  V2QsCollapsibleGroup,
  V2QsCollapsibleItem,
  defaultV2QsCollapsibleProps,
} from './types';

/**
 * V2QsCollapsible 컴포넌트
 *
 * 접이식 체크박스 그룹 컴포넌트
 * V1 QsCollapsible/QsCollapsibleGroup/QsCollapsibleGroupItem의 V2 버전으로 순수 UI만 담당
 *
 * 주요 기능:
 * - 접이식 그룹으로 체크박스 아이템 표시
 * - 특수 옵션 (NoneAbove, Special) 지원
 * - 아이템 클릭 시 상세 입력 모달 열기 콜백
 */
function V2QsCollapsible(props: V2QsCollapsibleProps) {
  const {
    groups,
    openGroupKey,
    onGroupToggle,
    onItemClick,
    onItemCheck,
    onSpecialOptionCheck,
    loadingGroupKey,
    disabled = defaultV2QsCollapsibleProps.disabled,
    readOnly = defaultV2QsCollapsibleProps.readOnly,
    hasError = defaultV2QsCollapsibleProps.hasError,
    errorMessage,
    loadingText = defaultV2QsCollapsibleProps.loadingText,
    addButtonText = defaultV2QsCollapsibleProps.addButtonText,
    editButtonText = defaultV2QsCollapsibleProps.editButtonText,
    className,
    groupClassName,
    itemClassName,
  } = props;

  // 그룹 토글
  const handleGroupToggle = useCallback(
    (groupKey: string) => {
      if (disabled || readOnly) return;
      onGroupToggle?.(groupKey);
    },
    [onGroupToggle, disabled, readOnly]
  );

  // 아이템 클릭
  const handleItemClick = useCallback(
    (groupKey: string, itemKey: string) => {
      if (disabled || readOnly) return;
      onItemClick?.(groupKey, itemKey);
    },
    [onItemClick, disabled, readOnly]
  );

  // 아이템 체크
  const handleItemCheck = useCallback(
    (groupKey: string, itemKey: string, checked: boolean) => {
      if (disabled || readOnly) return;
      onItemCheck?.(groupKey, itemKey, checked);
    },
    [onItemCheck, disabled, readOnly]
  );

  // 특수 옵션 체크
  const handleSpecialCheck = useCallback(
    (groupKey: string, checked: boolean) => {
      if (disabled || readOnly) return;
      onSpecialOptionCheck?.(groupKey, checked);
    },
    [onSpecialOptionCheck, disabled, readOnly]
  );

  // 특수 옵션 렌더링 (NoneAbove, Special)
  const renderSpecialOption = (group: V2QsCollapsibleGroup) => {
    return (
      <div
        key={group.key}
        className={classNames(
          'form-control',
          group.type === 'special' && 'px-2',
          groupClassName
        )}
      >
        <label className="label cursor-pointer gap-2 py-3">
          <span className="label-text text-base">{group.label}</span>
          <input
            type="checkbox"
            className={classNames(
              'checkbox checkbox-sm rounded',
              !group.disabled && !disabled && 'checkbox-primary'
            )}
            checked={group.checked || false}
            disabled={group.disabled || disabled || readOnly}
            onChange={(e) => handleSpecialCheck(group.key, e.target.checked)}
          />
        </label>
      </div>
    );
  };

  // TreeSx 타입 렌더링 (체크박스 + 라벨 + 버튼)
  // V1 QsCollapsibleGroupItem 스타일과 동일
  const renderTreeSxOption = (group: V2QsCollapsibleGroup) => {
    // TreeSx는 단일 아이템을 가짐 (items[0])
    const item = group.items?.[0];
    if (!item) return null;

    const isDisabled = item.disabled || disabled || readOnly;

    return (
      <div
        key={group.key}
        className={classNames(
          'form-control cursor-pointer',
          groupClassName
        )}
        onClick={() => {
          if (isDisabled) return;
          handleItemCheck(group.key, item.key, !item.checked);
        }}
      >
        <label
          className={classNames(
            'label gap-2 px-4 py-3 cursor-pointer',
            item.checked && 'bg-primary bg-opacity-5 rounded-lg'
          )}
        >
          <div className="flex-row gap-3 items-center">
            <input
              type="checkbox"
              checked={item.checked}
              disabled={isDisabled}
              className={classNames(
                'checkbox checkbox-lg rounded',
                !isDisabled && 'checkbox-primary'
              )}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation();
                handleItemCheck(group.key, item.key, e.target.checked);
              }}
            />
            <span className="label-text text-base font-medium">{item.label}</span>
          </div>
          {!readOnly && !disabled && (
            <>
              {!item.checked ? (
                <div className="flex gap flex-nowrap items-center">
                  <TbCirclePlus size={20} />
                </div>
              ) : (
                <div className="flex gap flex-nowrap ml-auto items-center">
                  <button
                    type="button"
                    className="btn btn-ghost px-0"
                    title={editButtonText}
                    aria-label={editButtonText}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleItemClick(group.key, item.key);
                    }}
                  >
                    <BiPencil size={22} className="cursor-pointer" />
                  </button>
                </div>
              )}
            </>
          )}
        </label>
      </div>
    );
  };

  // 아이템 렌더링
  const renderItem = (group: V2QsCollapsibleGroup, item: V2QsCollapsibleItem) => {
    const isDisabled = item.disabled || disabled || readOnly;
    return (
      <div
        key={item.key}
        className={classNames(
          'form-control cursor-pointer',
          itemClassName
        )}
        onClick={() => {
          if (isDisabled) return;
          handleItemCheck(group.key, item.key, !item.checked);
        }}
      >
        <label
          className={classNames(
            'label gap-2 px-4 py-3 cursor-pointer',
            item.checked && 'bg-primary bg-opacity-5 rounded-lg'
          )}
        >
          <div className="flex-row gap-3 items-center">
            <input
              type="checkbox"
              checked={item.checked}
              disabled={isDisabled}
              className={classNames(
                'checkbox checkbox-lg rounded',
                !item.disabled && !disabled && 'checkbox-primary'
              )}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation();
                handleItemCheck(group.key, item.key, e.target.checked);
              }}
            />
            <span className="label-text text-base font-medium">{item.label}</span>
          </div>
          {!readOnly && !disabled && (
            <>
              {!item.checked ? (
                <div className="flex gap flex-nowrap items-center">
                  <TbCirclePlus size={20} />
                </div>
              ) : (
                <div className="flex gap flex-nowrap ml-auto items-center">
                  <button
                    type="button"
                    className="btn btn-ghost px-0"
                    title={editButtonText}
                    aria-label={editButtonText}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleItemClick(group.key, item.key);
                    }}
                  >
                    <BiPencil size={22} className="cursor-pointer" />
                  </button>
                </div>
              )}
            </>
          )}
        </label>
      </div>
    );
  };

  // 접이식 그룹 렌더링
  const renderCollapsibleGroup = (group: V2QsCollapsibleGroup) => {
    const isOpen = openGroupKey === group.key;
    const isLoading = loadingGroupKey === group.key;

    return (
      <div
        key={group.key}
        className={classNames(
          'collapse collapse-arrow bg-base-100 rounded-none',
          'border-b border-t-0',
          isOpen && 'collapse-open',
          hasError && 'border-error',
          groupClassName
        )}
      >
        <div
          className="cursor-pointer collapse-title text-base font-medium"
          onClick={() => handleGroupToggle(group.key)}
        >
          {group.label}
        </div>
        <div className="collapse-content py-0 pl-3 pr-0">
          {isLoading && (
            <div className="flex items-center gap-2 py-4">
              <span className="loading loading-spinner loading-sm" />
              {loadingText && <span className="text-sm">{loadingText}</span>}
            </div>
          )}

          {!isLoading &&
            group.items?.map((item) => renderItem(group, item))}
        </div>
      </div>
    );
  };

  return (
    <div className={classNames('flex-1', className)}>
      {groups.map((group) => {
        // 특수 옵션 (NoneAbove, Special)
        if (group.type === 'noneAbove' || group.type === 'special') {
          return renderSpecialOption(group);
        }

        // TreeSx 타입 (단일 체크박스 아이템)
        if (group.type === 'treeSx') {
          return renderTreeSxOption(group);
        }

        // 일반 접이식 그룹
        return renderCollapsibleGroup(group);
      })}

      {/* 에러 메시지 */}
      {hasError && errorMessage && (
        <p className="text-error text-sm mt-2">{errorMessage}</p>
      )}
    </div>
  );
}

export default memo(V2QsCollapsible);
